import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setToken } from "../feature/token/tokenSlice";
import { setLanguage } from "../feature/language/languageSlice";
import { i18n } from "../../translations/i18n";
import * as API from "../service/api";
import { setUser } from "../feature/user/userSlice";

const LANG_REGEX = /^(en|id)$/i;

// Static sub-path yang bukan token (entry tidak punya param lain, jadi ini adalah sub-route)
// Jika candidateToken == salah satu ini, jangan treat sebagai token
const STATIC_SUB_SEGMENTS = new Set([
  "reward",
  "violation",
  "create",
  "mutation",
  "report",
  "detail",
  "form",
  "scan",
  "booking",
  "status",
  "create-qr",
]);

/**
 * Global handler untuk token & lang dari URL.
 * Entry endpoint invariant: tidak ada params lain selain token & lang,
 * sehingga pattern selalu /{base}/{token}/{lang} dengan 3 segment.
 * Contoh: /home/eyJ.../en , /saving/abc123/id , /perizinan/xyz/en
 *
 * Dipasang sekali di App.tsx, otomatis berlaku untuk semua entry
 * termasuk menu baru tanpa perlu duplikasi route /:token/:lang.
 */
export function useGlobalTokenLang() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lastHandledRef = useRef<string>("");

  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);

    // Entry hanya 3 segment: base / token / lang
    if (segments.length !== 3) return;

    const [base, candidateToken, candidateLang] = segments;

    if (!candidateToken || !candidateLang) return;
    if (!LANG_REGEX.test(candidateLang)) return;
    if (STATIC_SUB_SEGMENTS.has(candidateToken)) return;

    // Hindari loop: pathname sama sudah di-handle
    const cacheKey = `${base}/${candidateToken}/${candidateLang.toLowerCase()}`;
    if (lastHandledRef.current === cacheKey) return;

    const normalizedLang = candidateLang.toLowerCase();

    // Cek apakah sudah sama dengan yang tersimpan -> tetap bersihkan URL
    const storedTokenRaw = localStorage.getItem("token_redux");
    let storedToken: string | null = null;
    try {
      storedToken = storedTokenRaw ? JSON.parse(storedTokenRaw) : storedTokenRaw?.replaceAll('"', "") ?? null;
      if (storedToken) storedToken = String(storedToken).replaceAll('"', "");
    } catch {
      storedToken = storedTokenRaw?.replaceAll('"', "") ?? null;
    }
    const storedLangRaw = localStorage.getItem("language_redux");
    let storedLang: string | null = null;
    try {
      storedLang = storedLangRaw ? JSON.parse(storedLangRaw) : null;
      if (storedLang) storedLang = String(storedLang).toLowerCase();
    } catch {
      storedLang = storedLangRaw?.toLowerCase() ?? null;
    }

    const isSameToken = storedToken === candidateToken;
    const isSameLang = storedLang === normalizedLang;

    // Jika sudah sama, cukup bersihkan URL tanpa credentialCheck ulang
    if (isSameToken && isSameLang) {
      lastHandledRef.current = cacheKey;
      navigate(`/${base}${location.search}${location.hash}`, { replace: true });
      return;
    }

    // Simpan token & lang secara global
    dispatch(setToken(candidateToken));
    // Fallback direct (jaga sync untuk bearer() yang baca localStorage langsung)
    try {
      localStorage.setItem("token_redux", JSON.stringify(candidateToken));
    } catch {}

    i18n.changeLanguage(normalizedLang);
    dispatch(setLanguage(normalizedLang));

    lastHandledRef.current = cacheKey;

    // Validasi token ke backend, lalu bersihkan URL
    API.credentialCheck(candidateToken)
      .then((res: any) => {
        if (res?.data?.user) {
          dispatch(setUser(res.data.user));
        }
        navigate(`/${base}${location.search}${location.hash}`, { replace: true });
      })
      .catch(() => {
        // credentialCheck gagal -> tetap bersihkan URL agar tidak loop,
        // biarkan page/component handle redirect ke /login jika perlu
        navigate(`/${base}${location.search}${location.hash}`, { replace: true });
      });
  }, [location.pathname, location.search, location.hash, dispatch, navigate]);
}
